/** Profils d'IA = réglages (pas de triche : l'IA produit le MÊME CarInput qu'un humain). */
export interface AiProfile {
  /** Distance de visée de base (px d'arc) pour le suivi de trajectoire. */
  lookAheadBase: number;
  /** Distance de visée ajoutée par px/s de vitesse (regarde plus loin quand ça va vite). */
  lookAheadPerSpeed: number;
  /** Plafond de la distance de visée (px). */
  lookAheadMax: number;
  /** Gain de braquage (proportionnel à l'erreur d'angle, en rad). */
  kSteer: number;
  /** Amortissement anti-oscillation (sur la vitesse angulaire). */
  kDamp: number;
  /** Accélération latérale max acceptée en virage (px/s²) → vitesse de virage = √(aLatMax/courbure). */
  aLatMax: number;
  /** Décélération supposée pour anticiper le freinage avant un virage (px/s²). */
  brakeDecel: number;
  /** Horizon (px d'arc) sur lequel on cherche les virages à venir. */
  brakeHorizon: number;
  /** Plafond de vitesse (px/s) — bride la difficulté. */
  speedCap: number;

  // --- Perception des autres voitures (M5) ---
  /** Distance devant (px d'arc) où l'IA prend en compte une voiture à éviter/suivre. */
  avoidAhead: number;
  /** Marge latérale (px) : une voiture dont l'écart latéral est < clearance est « dans mon couloir ». */
  clearance: number;
  /** Amplitude max du décalage d'évitement (px le long de la normale de piste). */
  avoidStrength: number;

  // --- Difficulté sans triche (M5) ---
  /** Latence de réaction : les inputs sont appliqués avec ce retard (pas de simulation). */
  reactionDelaySteps: number;
  /** Probabilité, par pas, de déclencher une petite erreur de conduite (braquage + hésitation). */
  mistakeRate: number;
}

// `normal` reste PARFAITEMENT crisp (0 latence, 0 erreur) : c'est la baseline propre — et ça garde
// le test « une IA boucle en restant en piste » identique. La difficulté vécue vient de l'écart
// fast (adversaire coriace) ↔ slow (adversaire facile : plus lent, réagit tard, fait des fautes).
export const AI_PROFILES: Record<string, AiProfile> = {
  normal: {
    lookAheadBase: 55, lookAheadPerSpeed: 0.30, lookAheadMax: 150,
    kSteer: 2.6, kDamp: 0.22, aLatMax: 360, brakeDecel: 1800, brakeHorizon: 200, speedCap: 360,
    avoidAhead: 110, clearance: 26, avoidStrength: 30, reactionDelaySteps: 0, mistakeRate: 0,
  },
  fast: {
    lookAheadBase: 60, lookAheadPerSpeed: 0.30, lookAheadMax: 160,
    kSteer: 2.6, kDamp: 0.22, aLatMax: 460, brakeDecel: 2100, brakeHorizon: 200, speedCap: 380,
    avoidAhead: 120, clearance: 26, avoidStrength: 32, reactionDelaySteps: 0, mistakeRate: 0,
  },
  slow: {
    lookAheadBase: 50, lookAheadPerSpeed: 0.28, lookAheadMax: 140,
    kSteer: 2.5, kDamp: 0.24, aLatMax: 270, brakeDecel: 1500, brakeHorizon: 200, speedCap: 300,
    avoidAhead: 100, clearance: 24, avoidStrength: 26, reactionDelaySteps: 5, mistakeRate: 0.010,
  },
};

export function getAiProfile(name?: string | null): AiProfile {
  return AI_PROFILES[name ?? 'normal'] ?? AI_PROFILES.normal;
}
